import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Loader2, Maximize2, X } from 'lucide-react';
import api from '@/lib/axiosInstance';
import Helper from '@/lib/helper';
import { Button } from '@/components/ui/button';

type ManualFile = {
    id: number;
    fileKind: 'pdf' | 'image';
    name: string;
    objectKey?: string;
    url?: string;
};

const s3BaseUrl = `${import.meta.env.VITE_AWS_S3 || ''}`.replace(/\/$/, '');

const resolveUrl = (path?: string | null) => {
    if (!path) return '';
    if (/^(https?:|blob:)/.test(path)) return path;
    if (!s3BaseUrl) return path;
    return `${s3BaseUrl}/${path.replace(/^\//, '')}`;
};

async function logInteraction(fileId: number, payload: { event: string }) {
    try {
        await api.post(`/agent-handbook/manual-files/${fileId}/interaction`, payload, {
            headers: { 'Content-Type': 'application/json' },
        });
    } catch {
        /* optional endpoint */
    }
}

const ManualByIdPage = () => {
    const { documentId } = useParams<{ documentId: string }>();
    const [file, setFile] = useState<ManualFile | null>(null);
    const [sectionTitle, setSectionTitle] = useState<string | null>(null);
    const [sectionContent, setSectionContent] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        const id = documentId;
        if (!id) {
            setError('Missing document id.');
            setLoading(false);
            return;
        }

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                try {
                    const detail = await api.get(`/agent-handbook/manual-files/${id}`);
                    if (detail.data?.status && detail.data.file) {
                        setFile(detail.data.file as ManualFile);
                        if (detail.data.sectionTitle) setSectionTitle(String(detail.data.sectionTitle));
                        if (detail.data.sectionContent) setSectionContent(String(detail.data.sectionContent));
                        void logInteraction(Number(id), { event: 'page_view' });
                        return;
                    }
                } catch {
                    /* try dashboard fallback */
                }

                const dash = await api.get('/agent-handbook');
                if (!dash.data?.status) throw new Error(dash.data?.error || 'Failed to load manuals.');
                const sections = dash.data.manualSections || [];
                for (const s of sections) {
                    const f = (s.files || []).find((x: ManualFile) => String(x.id) === String(id));
                    if (f) {
                        setFile(f);
                        setSectionTitle(s.title || null);
                        setSectionContent(s.content || null);
                        void logInteraction(Number(id), { event: 'page_view' });
                        return;
                    }
                }
                throw new Error('Manual file not found.');
            } catch (err: any) {
                setError(Helper.errorHandler(err));
            } finally {
                setLoading(false);
            }
        };

        void load();
    }, [documentId]);

    const href = file ? resolveUrl(file.objectKey || file.url) : '';
    const displayTitle = sectionTitle || file?.name || 'Manual';

    if (loading) {
        return (
            <div className="flex min-h-[50vh] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (error || !file) {
        return (
            <div className="mx-auto max-w-lg space-y-4 p-6">
                <Button variant="ghost" size="sm" asChild>
                    <Link to="/agent-handbook?tab=manual" className="inline-flex items-center gap-2">
                        <ArrowLeft className="h-4 w-4" />
                        Back to manual
                    </Link>
                </Button>
                <p className="text-sm text-destructive">{error || 'Not found.'}</p>
            </div>
        );
    }

    return (
        <div className="flex min-h-[calc(100vh-4rem)] flex-col bg-muted/30">
            <header className="flex shrink-0 items-center gap-3 border-b border-border bg-background px-3 py-2 sm:px-4">
                <Button variant="ghost" size="sm" className="px-2 text-muted-foreground" asChild>
                    <Link to="/agent-handbook?tab=manual" className="inline-flex items-center gap-1.5">
                        <ArrowLeft className="h-4 w-4" />
                        <span className="hidden sm:inline">Back</span>
                    </Link>
                </Button>
                <div className="min-w-0 flex-1">
                    <h1 className="truncate text-sm font-semibold leading-tight text-foreground sm:text-base">{displayTitle}</h1>
                    {file.name && file.name !== displayTitle && (
                        <p className="truncate text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                            {file.name}
                        </p>
                    )}
                </div>
                <Button variant="outline" size="sm" className="px-2 sm:px-3" asChild>
                    <a href={href || '#'} download={file.name} target="_blank" rel="noreferrer">
                        <Download className="h-4 w-4" />
                        <span className="hidden sm:inline">Download</span>
                    </a>
                </Button>
            </header>

            {sectionContent && (
                <div className="shrink-0 border-b border-border bg-background px-3 py-3 sm:px-4">
                    <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{sectionContent}</p>
                </div>
            )}

            <div className="flex flex-1 flex-col overflow-hidden">
                {file.fileKind === 'image' ? (
                    <div className="flex flex-1 flex-col items-center justify-start gap-2 overflow-auto bg-muted/20 p-4 sm:p-6">
                        <button type="button" onClick={() => setExpanded(true)} className="group relative cursor-zoom-in">
                            <img src={href} alt={displayTitle} className="max-h-[55vh] max-w-full rounded-md object-contain shadow-md transition group-hover:opacity-95" />
                            <span className="absolute bottom-2 right-2 flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-[11px] font-medium text-white">
                                <Maximize2 className="h-3 w-3" />
                                Tap to expand
                            </span>
                        </button>
                    </div>
                ) : (
                    <iframe
                        title={displayTitle}
                        src={`${href}#toolbar=0&navpanes=0`}
                        className="min-h-0 w-full flex-1 border-0 bg-white"
                    />
                )}
            </div>

            {expanded && file.fileKind === 'image' && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
                    onClick={() => setExpanded(false)}
                >
                    <button
                        type="button"
                        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
                        onClick={() => setExpanded(false)}
                    >
                        <X className="h-5 w-5" />
                    </button>
                    <img
                        src={href}
                        alt={displayTitle}
                        className="max-h-full max-w-full object-contain"
                        onClick={e => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
};

export default ManualByIdPage;
